import { SearchEngineFilters } from '@/app/(routes)/[locale]/(unauth)/oldrecherche/components/v2/filters/config';
import type { InitialLabels } from '@/components/forms/CreateAlertForm/AlertSearchInterface';
import type { EntityType } from '@/components/ProfileHeader/types';
import { SUB_UNIVERSE_ATTRIBUTE } from '@/lib/search/searchAttributes';

/** Defensive cap on the number of relations created in a single alert. */
export const MAX_ENTITY_IDS = 200;

/**
 * Directus M2M relation field on the `alertes` collection for a given entity.
 */
export type EntityRelationField =
  | 'auteurs'
  | 'editeurs'
  | 'relieurs'
  | 'lieux_edition'
  | 'selections'
  | 'sous_univers';

/**
 * Directus junction id field for a given entity relation.
 */
export type EntityIdField =
  | 'auteurs_id'
  | 'editeurs_id'
  | 'relieurs_id'
  | 'lieux_edition_id'
  | 'selections_id'
  | 'sous_univers_id';

/**
 * Meilisearch attribute used to pre-fill the advanced alert search state.
 *
 * `sous_univers` does NOT use `SearchEngineFilters.SousUnivers` (`'sous_univers'`,
 * incorrect). The real Meilisearch attribute is `SUB_UNIVERSE_ATTRIBUTE`
 * (`'univers.sous_univers_id.id'`) — see `lib/search/searchAttributes.ts`.
 */
export type EntitySearchFilterKey =
  | SearchEngineFilters
  | typeof SUB_UNIVERSE_ATTRIBUTE;

/**
 * i18n key (under `Blocs.FormulaireInscriptionAlerteEntite`) for the intro text.
 */
export type EntityIntroKey =
  | 'intro_auteur'
  | 'intro_editeur'
  | 'intro_relieur'
  | 'intro_lieu_edition'
  | 'intro_selection'
  | 'intro_sous_univers';

/**
 * i18n key (under `Blocs.FormulaireInscriptionAlerteEntite`) for the success
 * confirmation message.
 */
export type EntityConfirmKey =
  | 'Messages.confirmation_inscription_auteur'
  | 'Messages.confirmation_inscription_editeur'
  | 'Messages.confirmation_inscription_relieur'
  | 'Messages.confirmation_inscription_lieu_edition'
  | 'Messages.confirmation_inscription_selection'
  | 'Messages.confirmation_inscription_sous_univers';

/**
 * Everything needed to subscribe to alerts for a single entity type, in one
 * place. Sourced from this table by the server action, the modal, and the form
 * instead of duplicated `switch` statements.
 */
export interface EntityAlertConfig {
  /** Directus M2M relation field on `alertes`. */
  relationField: EntityRelationField;
  /** Directus junction id field for that relation. */
  idField: EntityIdField;
  /** Meilisearch attribute used to pre-fill the advanced alert search state. */
  searchFilterKey: EntitySearchFilterKey;
  /** Key into `InitialLabels` for the advanced alert modal pre-fill. */
  labelKey: keyof InitialLabels;
  /** i18n key for the form intro text. */
  introKey: EntityIntroKey;
  /** i18n key for the success confirmation message. */
  confirmKey: EntityConfirmKey;
}

/**
 * Exhaustive, single source of truth keyed by `EntityType`. Because it is typed
 * as `Record<EntityType, EntityAlertConfig>`, adding a member to `EntityType`
 * without adding it here is a compile error — exhaustiveness by construction.
 *
 * Note: `sous_univers` does NOT use `SearchEngineFilters.SousUnivers`
 * (`'sous_univers'`, incorrect). The real Meilisearch attribute is
 * `'univers.sous_univers_id.id'` — see `lib/search/searchAttributes.ts`.
 */
export const ENTITY_ALERT_CONFIG: Record<EntityType, EntityAlertConfig> = {
  auteur: {
    relationField: 'auteurs',
    idField: 'auteurs_id',
    searchFilterKey: SearchEngineFilters.Auteurs,
    labelKey: 'authors',
    introKey: 'intro_auteur',
    confirmKey: 'Messages.confirmation_inscription_auteur',
  },
  editeur: {
    relationField: 'editeurs',
    idField: 'editeurs_id',
    searchFilterKey: SearchEngineFilters.Editeurs,
    labelKey: 'editors',
    introKey: 'intro_editeur',
    confirmKey: 'Messages.confirmation_inscription_editeur',
  },
  relieur: {
    relationField: 'relieurs',
    idField: 'relieurs_id',
    searchFilterKey: SearchEngineFilters.Relieurs,
    labelKey: 'binders',
    introKey: 'intro_relieur',
    confirmKey: 'Messages.confirmation_inscription_relieur',
  },
  lieu_edition: {
    relationField: 'lieux_edition',
    idField: 'lieux_edition_id',
    searchFilterKey: SearchEngineFilters.LieuxEdition,
    labelKey: 'publicationPlaces',
    introKey: 'intro_lieu_edition',
    confirmKey: 'Messages.confirmation_inscription_lieu_edition',
  },
  selection: {
    relationField: 'selections',
    idField: 'selections_id',
    searchFilterKey: SearchEngineFilters.Selections,
    labelKey: 'selections',
    introKey: 'intro_selection',
    confirmKey: 'Messages.confirmation_inscription_selection',
  },
  sous_univers: {
    relationField: 'sous_univers',
    idField: 'sous_univers_id',
    searchFilterKey: SUB_UNIVERSE_ATTRIBUTE,
    labelKey: 'subUniverses',
    introKey: 'intro_sous_univers',
    confirmKey: 'Messages.confirmation_inscription_sous_univers',
  },
};
